/* eslint-disable */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    BookOpen, ClipboardList, Search, X,
    Users, Building2, UserCheck, ChevronDown,
    ChevronLeft, ChevronRight,
} from 'lucide-react';
import moment from "moment";
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Page from '../../../components/Page';
import catalogSvc from '../../../services/clientAdmin/catalogServices';
import { getDepartments, getTeams } from '../../../services/clientAdmin/departmentServices';
import tenantUserSvc from '../../../services/clientAdmin/tenantUserServices';

const PAGE_SIZE = 10;

function fmt(v) {
    if (!v) return '—';
    return v.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function Pagination({ page, total, size, onChange }) {
    const totalPages = Math.max(1, Math.ceil(total / size));
    const from = total === 0 ? 0 : page * size + 1;
    const to = Math.min((page + 1) * size, total);
    return (
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-400">
                {total === 0 ? 'No results' : `${from}–${to} of ${total}`}
            </p>
            <div className="flex items-center gap-1">
                <button onClick={() => onChange(page - 1)} disabled={page === 0}
                    className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                    <ChevronLeft size={14} />
                </button>
                <span className="text-xs text-gray-600 px-2 font-medium">{page + 1} / {totalPages}</span>
                <button onClick={() => onChange(page + 1)} disabled={page >= totalPages - 1}
                    className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                    <ChevronRight size={14} />
                </button>
            </div>
        </div>
    );
}

function MultiSelect({ label, options, selected, onChange }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);
    const filtered = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
    const toggle = id => onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);

    useEffect(() => {
        function handleClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    return (
        <div className="relative" ref={ref}>
            <button type="button" onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2 text-sm text-left bg-white hover:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-300">
                <span className="text-gray-600">{selected.length === 0 ? label : `${selected.length} selected`}</span>
                <ChevronDown size={15} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                    <div className="p-2 border-b border-gray-100">
                        <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
                            placeholder="Search…"
                            className="w-full text-sm px-2 py-1.5 border border-gray-200 rounded focus:outline-none focus:border-purple-400" />
                    </div>
                    <ul className="max-h-48 overflow-y-auto py-1">
                        {filtered.length === 0 && <li className="px-3 py-2 text-sm text-gray-400">No results</li>}
                        {filtered.map(o => (
                            <li key={o.id} onClick={() => toggle(o.id)}
                                className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-purple-50 cursor-pointer select-none">
                                <input type="checkbox" readOnly checked={selected.includes(o.id)} className="accent-purple-600 flex-shrink-0" />
                                <span className="truncate">{o.label}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function ViewAssignDrawer({ item, onClose, onAssigned }) {
    const [tab, setTab] = useState('department');

    const clientId = useSelector((state) => state.userInfo?.clientId);

    const [departments, setDepts] = useState([]);
    const [teams, setTeams] = useState([]);
    const [users, setUsers] = useState([]);


    const [selDepts, setSelDepts] = useState([]);
    const [selTeams, setSelTeams] = useState([]);
    const [selUsers, setSelUsers] = useState([]);

    const [existingDepts, setExistingDepts] = useState([]);
    const [existingTeams, setExistingTeams] = useState([]);
    const [existingUsers, setExistingUsers] = useState([]);

    const [userSearch, setUserSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [allowProctoring, setAllowProctoring] = useState(false);
    const [enableSchedule, setEnableSchedule] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [fromTime, setFromTime] = useState('');
    const [toTime, setToTime] = useState('');

    useEffect(() => {
        setLoading(true);
        Promise.all([
            getDepartments(),
            tenantUserSvc.getTenantUsers(clientId, 0, 200, '', ''),
            catalogSvc.getAssignments(item.sccpId, item.itemId),
        ]).then(([dRes, uRes, aRes]) => {

            if (dRes.ok) setDepts((dRes.data?.departments || dRes.data || []).filter(d => d.isActive));
            if (uRes.ok) {
                const all = uRes.data?.users || uRes.data || [];
                setUsers(all.filter(u => u.role !== 'CONTENT_WRITER' && u.role !== 'CLIENT_ADMIN' && u.role !== 'MANAGER'));
            }
            if (aRes.ok) {
                const data = aRes.data || {};

                const preDepts = (data.departments || []).map(d => d.id);
                const preTeams = (data.teams || []).map(t => t.id);
                const preUsers = (data.users || []).map(u => u.id);
                setExistingDepts(preDepts);
                setExistingTeams(preTeams);
                setExistingUsers(data.users);
                setSelDepts(preDepts);
                setSelTeams(preTeams);
                setSelUsers(preUsers);
                if (data.allowproctoring !== undefined) setAllowProctoring(data.allowproctoring);
                const schedule = data.users?.filter((val) => val?.scheduled_from_datetime && val?.scheduled_to_datetime)
                const splitDateTime = (isoString) => {
                    const dt = new Date(isoString);
                    const date = dt.toISOString().split("T")[0];
                    const time = dt.toISOString().split("T")[1].slice(0, 5);
                    return { date, time };
                };

                if (schedule && schedule?.length > 0) {

                    setEnableSchedule(true);
                    const fromParts = splitDateTime(schedule[0]?.scheduled_from_datetime);
                    const toParts = splitDateTime(schedule[0]?.scheduled_to_datetime);

                    setFromDate(fromParts.date);
                    setToDate(toParts.date);
                    setFromTime(fromParts.time);
                    setToTime(toParts.time);
                }
            }
        }).finally(() => setLoading(false));
    }, [item.sccpId]);

    useEffect(() => {
        if (tab !== 'team' || departments.length === 0) return;
        Promise.all(departments.map(d => getTeams(d.id)))
            .then(results => {
                const all = results.flatMap((r, i) =>
                    r.ok ? (r.data?.teams || r.data || [])
                        .filter(t => t.isActive)
                        .map(t => ({
                            ...t,
                            departmentId: departments[i].id,
                            departmentName: departments[i].name,
                        }))
                        : []
                );
                setTeams(all);
            });
    }, [tab, departments]);

    // ─── CASCADE: deselect dept → deselect its teams → deselect those teams' users ───
    const handleDeptToggle = (deptId) => {
        const isRemoving = selDepts.includes(deptId);

        if (isRemoving) {
            // 1. Remove dept
            setSelDepts(prev => prev.filter(id => id !== deptId));

            // 2. Find all teams belonging to this dept
            const deptTeamIds = teams
                .filter(t => t.departmentId === deptId)
                .map(t => t.id);

            // 3. Remove those teams
            setSelTeams(prev => prev.filter(id => !deptTeamIds.includes(id)));

            // 4. Remove all users belonging to this dept
            setSelUsers(prev => prev.filter(uid => {
                const user = users.find(u => u.id === uid);
                return user ? user.departmentId !== deptId : true;
            }));
        } else {
            setSelDepts(prev => [...prev, deptId]);
        }
    };

        const handleTeamToggle = (teamId) => {
    const isRemoving = selTeams.includes(teamId);

    if (isRemoving) {
        // 1. Remove team
        setSelTeams(prev => prev.filter(id => id !== teamId));

        // 2. Remove all users belonging to this team
        setSelUsers(prev => prev.filter(uid => {
            const user = users.find(u => u.id === uid);
            return user ? user.teamId !== teamId : true;
        }));
    } else {
        // 1. Add team
        setSelTeams(prev => [...prev, teamId]);

        // 2. Auto-select all users belonging to this team
        setSelUsers(prev => {
            const teamUserIds = users
                .filter(u => u.teamId === teamId)
                .map(u => u.id);
            return [...new Set([...prev, ...teamUserIds])];
        });
    }
};


//     const handleDeptToggle = (deptId) => {
//     const isRemoving = selDepts.includes(deptId);

//     if (isRemoving) {
//         // 1. Remove dept
//         setSelDepts(prev => prev.filter(id => id !== deptId));

//         // 2. Find all teams belonging to this dept
//         const deptTeamIds = teams
//             .filter(t => t.departmentId === deptId)
//             .map(t => t.id);

//         // 3. Remove those teams
//         setSelTeams(prev => prev.filter(id => !deptTeamIds.includes(id)));

//         // 4. Remove all users belonging to this dept
//         setSelUsers(prev => prev.filter(uid => {
//             const user = users.find(u => u.id === uid);
//             return user ? user.departmentId !== deptId : true;
//         }));
//     } else {
//         // 1. Add dept
//         setSelDepts(prev => [...prev, deptId]);

//         // 2. Auto-select all users belonging to this dept
//         setSelUsers(prev => {
//             const deptUserIds = users
//                 .filter(u => u.departmentId === deptId)
//                 .map(u => u.id);
//             return [...new Set([...prev, ...deptUserIds])];
//         });
//     }
// };
    // ─── CASCADE: deselect team → deselect its users ───
    // const handleTeamToggle = (teamId) => {
    //     const isRemoving = selTeams.includes(teamId);

    //     if (isRemoving) {
    //         // 1. Remove team
    //         setSelTeams(prev => prev.filter(id => id !== teamId));

    //         // 2. Remove all users belonging to this team
    //         setSelUsers(prev => prev.filter(uid => {
    //             const user = users.find(u => u.id === uid);
    //             return user ? user.teamId !== teamId : true;
    //         }));
    //     } else {
    //         setSelTeams(prev => [...prev, teamId]);
    //     }
    // };

    const scheduleError = (() => {
        if (!enableSchedule) return '';
        if (!fromDate || !toDate || !fromTime || !toTime) return 'Please fill in all schedule fields.';
        const start = new Date(`${fromDate}T${fromTime}`);
        const end = new Date(`${toDate}T${toTime}`);
        if (end <= start) return 'End date/time must be after start date/time.';
        return '';
    })();

    const handleAssign = async () => {
        const type = tab;
        const ids = type === 'department' ? selDepts
            : type === 'team' ? selTeams
                : selUsers;

        if (item.type === 'assessment' && enableSchedule && scheduleError) {
            setError(scheduleError);
            return;
        }

        setSubmitting(true);
        setError('');
        const res = await catalogSvc.assignContent({
            sccpId: item.sccpId,
            itemId: item.itemId,
            type,
            ids,
            isCourse: item.type === 'course',
            allowProctoring: item.type === 'assessment' ? allowProctoring : undefined,
            ...(item.type === 'assessment' && enableSchedule
                ? { fromDate, toDate, fromTime, toTime }
                : {}),
        });
        setSubmitting(false);
        if (res.ok) onAssigned(res.data);
        else setError(res.data?.message || 'Assignment failed. Please try again.');
    };

    const filteredUsers = users.filter(u =>
        `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(userSearch.toLowerCase())
    );

    const allSelected =
        filteredUsers.length > 0 &&
        filteredUsers.every((u) => selUsers.includes(u.id))


    const handleSelectAll = () => {
        if (allSelected) {
            setSelUsers(prev => prev.filter(id => !filteredUsers.some(u => u.id === id)));
        } else {
            setSelUsers(prev => [...new Set([...prev, ...filteredUsers.map(u => u.id)])]);
        }
    };

    const handleTabChange = (newTab) => {
        setTab(newTab);
        setError('');
    };

    const ASSIGN_TABS = [
        { key: 'department', label: 'Department', icon: Building2 },
        { key: 'team', label: 'Team', icon: Users },
        { key: 'individual', label: 'Individual', icon: UserCheck },
    ];

    const available = item.licensesAllocated != null
        ? Math.max(0, item.licensesAllocated - item.licensesAssigned)
        : '∞';

    const newCountFor = {
        department: selDepts.length,
        team: selTeams.length,
        individual: selUsers.length,
    };

    return (
        <>
            <div className="fixed top-[65px] inset-x-0 bottom-0 bg-black/40 z-40" onClick={onClose} />
            <div className="fixed top-[65px] right-0 bottom-0 z-50 w-full sm:w-[460px] bg-white shadow-2xl flex flex-col">

                {/* Header */}
                <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                        {item.type === 'assessment'
                            ? <ClipboardList size={18} className="text-purple-500 flex-shrink-0 mt-0.5" />
                            : <BookOpen size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />}
                        <div className="min-w-0">
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">
                                {item.type === 'assessment' ? 'Assessment' : 'Course'}
                            </p>
                            <h3 className="text-sm font-semibold text-gray-900 leading-snug">{item.title}</h3>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 flex-shrink-0 ml-3">
                        <X size={18} className="text-gray-500" />
                    </button>
                </div>

                {/* Details strip */}
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                    <div className="grid grid-cols-3 gap-3 text-xs">
                        <div>
                            <p className="text-gray-400 font-medium uppercase tracking-wide mb-0.5">Format</p>
                            <p className="text-gray-700 font-medium">{fmt(item.format) || '—'}</p>
                        </div>
                        <div>
                            <p className="text-gray-400 font-medium uppercase tracking-wide mb-0.5">Source</p>
                            <span className={`inline-block px-2 py-0.5 rounded-full font-medium text-xs ${item.origin === 'org'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-purple-100 text-purple-700'
                                }`}>
                                {item.origin === 'org' ? 'Your Organisation' : 'keySkillset'}
                            </span>
                        </div>
                        <div>
                            <p className="text-gray-400 font-medium uppercase tracking-wide mb-0.5">Assigned</p>
                            <p className="text-gray-700 font-medium">{item.licensesAssigned}</p>
                        </div>
                    </div>
                </div>

                {/* License bar */}
                <div className="px-5 py-2.5 bg-purple-50 border-b border-purple-100 flex gap-5 text-xs text-purple-700 flex-shrink-0">
                    <span>Allocated: <strong>{item.licensesAllocated ?? '∞'}</strong></span>
                    <span>Assigned: <strong>{item.licensesAssigned}</strong></span>
                    <span>Available: <strong>{available}</strong></span>
                </div>

                {/* Assign-to label */}
                <div className="px-5 pt-4 pb-0 flex-shrink-0">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Assign to</p>
                </div>

                {/* Type tabs */}
                <div className="flex border-b border-gray-200 px-5 flex-shrink-0">
                    {ASSIGN_TABS.map(t => (
                        <button
                            key={t.key}
                            onClick={() => handleTabChange(t.key)}
                            className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.key
                                ? 'border-purple-600 text-purple-700'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <t.icon size={13} />
                            {t.label}
                            {newCountFor[t.key] > 0 && (
                                <span className="ml-1 text-xs bg-purple-600 text-white rounded-full px-1.5 py-0.5 leading-none">
                                    {newCountFor[t.key]}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto px-5 py-4">
                    {loading ? (
                        <div className="flex items-center justify-center h-32">
                            <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : tab === 'department' ? (
                        // ─── DEPARTMENT TAB ───
                        <div className="space-y-3">
                            <p className="text-xs text-gray-400">
                                Deselecting a department will also deselect its teams and users.
                            </p>
                            <MultiSelect
                                label="Choose departments…"
                                options={departments.map(d => ({ id: d.id, label: d.name }))}
                                selected={selDepts}
                                onChange={(newSel) => {
                                    // figure out which dept was removed
                                    const removed = selDepts.filter(id => !newSel.includes(id));
                                    if (removed.length > 0) {
                                        removed.forEach(deptId => handleDeptToggle(deptId));
                                    } else {
                                        // something was added
                                        const added = newSel.filter(id => !selDepts.includes(id));
                                        added.forEach(deptId => handleDeptToggle(deptId));
                                    }
                                }}
                            />
                            {selDepts.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 pt-1">
                                    {departments.filter(d => selDepts.includes(d.id)).map(d => (
                                        <span key={d.id} className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                                            {d.name}
                                            <button onClick={() => handleDeptToggle(d.id)}>
                                                <X size={11} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : tab === 'team' ? (
                        // ─── TEAM TAB ───
                        <div className="space-y-3">
                            <p className="text-xs text-gray-400">
                                Deselecting a team will also deselect its users.
                            </p>
                            {(() => {
                                const grouped = departments.reduce((acc, dept) => {
                                    const deptTeams = teams.filter(t => t.departmentId === dept.id);
                                    if (deptTeams.length > 0) acc.push({ dept, teams: deptTeams });
                                    return acc;
                                }, []);

                                if (grouped.length === 0) {
                                    return (
                                        <p className="text-xs text-gray-400 text-center py-6">
                                            No teams found. Create teams in the Departments section.
                                        </p>
                                    );
                                }

                                return (
                                    <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[380px] overflow-y-auto">
                                        {grouped.map(({ dept, teams: deptTeams }) => (
                                            <div key={dept.id}>
                                                <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 border-b border-gray-200 sticky top-0 z-10">
                                                    <Building2 size={13} className="text-gray-500 flex-shrink-0" />
                                                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                                        {dept.name}
                                                    </span>
                                                    <span className="ml-auto text-xs text-gray-400">
                                                        {deptTeams.length} team{deptTeams.length !== 1 ? 's' : ''}
                                                    </span>
                                                </div>
                                                {deptTeams.map(t => {
                                                    const isSelected = selTeams.includes(t.id);
                                                    return (
                                                        <div
                                                            key={t.id}
                                                            onClick={() => handleTeamToggle(t.id)}
                                                            className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 last:border-b-0 select-none hover:bg-purple-50 cursor-pointer"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => { }}
                                                                className="accent-purple-600 flex-shrink-0"
                                                            />
                                                            <div className="min-w-0 flex-1">
                                                                <p className="text-sm font-medium text-gray-800 truncate">{t.name}</p>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}

                            {selTeams.length > 0 && (
                                <div className="space-y-1">
                                    <p className="text-xs text-purple-600 font-medium">
                                        {selTeams.length} team(s) selected
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {teams.filter(t => selTeams.includes(t.id)).map(t => (
                                            <span key={t.id} className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                                                {t.name}
                                                <span className="text-purple-400 text-[10px]">({t.departmentName})</span>
                                                <button onClick={() => handleTeamToggle(t.id)}>
                                                    <X size={11} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        // ─── INDIVIDUAL TAB ───
                        <div className="space-y-3">
                            <p className="text-xs text-gray-400">Search and select individual learners.</p>

                            <div className="relative">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    value={userSearch}
                                    onChange={(e) => setUserSearch(e.target.value)}
                                    placeholder="Search by name or email…"
                                    className="w-full text-sm pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200"
                                />
                            </div>

                            {filteredUsers.length > 0 && (
                                <div className="flex items-center gap-2 px-1">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        onChange={handleSelectAll}
                                        className="accent-purple-600"
                                    />
                                    <span className="text-sm font-medium text-gray-700">Select All</span>
                                </div>
                            )}

                            <ul className="max-h-[420px] overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-lg">
                                {filteredUsers.length === 0 ? (
                                    <li className="px-3 py-4 text-sm text-gray-400 text-center">
                                        {userSearch ? "No learners match your search." : "No learners available."}
                                    </li>
                                ) : (
                                    filteredUsers.map((u) => {
                                        const isSelected = selUsers.includes(u.id);
                                        const scheduled = existingUsers.find((data) => data.id === u.id)
                                        return (
                                            <li
                                                key={u.id}
                                                onClick={() =>
                                                    setSelUsers(prev =>
                                                        prev.includes(u.id)
                                                            ? prev.filter(id => id !== u.id)
                                                            : [...prev, u.id]
                                                    )
                                                }
                                                className="flex items-center gap-3 px-3 py-2.5 select-none hover:bg-purple-50 cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => { }}
                                                    className="accent-purple-600 flex-shrink-0"
                                                />

                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-medium text-gray-800 truncate">
                                                        {u.firstName} {u.lastName}
                                                        {item.type === 'assessment' &&
                                                        <span className='inline-block px-2 py-0.5 rounded-full font-medium text-xs bg-purple-100 text-purple-700 '>
                                                            {(scheduled?.scheduled_from_datetime && scheduled?.scheduled_to_datetime) ? 'scheduled' : 'Unscheduled'}
                                                        </span>}
                                                    </p>
                                                    <p className="text-xs text-gray-500 truncate">{u.email}</p>
                                                    {(scheduled?.scheduled_from_datetime && scheduled?.scheduled_to_datetime) && <p className="text-xs text-gray-500 truncate">{moment.utc(scheduled?.scheduled_from_datetime).format("LL")} -{moment.utc(scheduled?.scheduled_to_datetime).format("LL")}</p>}


                                                </div>
                                            </li>
                                        );
                                    })
                                )}
                            </ul>

                            {selUsers.length > 0 && (
                                <p className="text-xs text-purple-600 font-medium">
                                    {selUsers.length} learner(s) selected
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 px-5 py-4 space-y-3 flex-shrink-0">
                    {item.type === 'assessment' && (
                        <>
                            <div className="flex items-center justify-between py-2">
                                <span className="text-sm font-medium text-gray-700">Require Proctoring</span>
                                <button type="button" onClick={() => setAllowProctoring(prev => !prev)}
                                    className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${allowProctoring ? 'bg-purple-600' : 'bg-gray-300'}`}>
                                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${allowProctoring ? 'translate-x-5' : 'translate-x-0'}`} />
                                </button>
                            </div>

                            {/* <div className="flex items-center justify-between py-2">
                                <span className="text-sm font-medium text-gray-700">Time Schedule</span>
                                <button type="button" onClick={() => setEnableSchedule(prev => !prev)}
                                    className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${enableSchedule ? 'bg-purple-600' : 'bg-gray-300'}`}>
                                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${enableSchedule ? 'translate-x-5' : 'translate-x-0'}`} />
                                </button>
                            </div> */}

                            {(enableSchedule && process.env.REACT_APP_ENV === 'dev' )&& (
                                <div className="grid grid-cols-2 gap-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">
                                            From Date
                                        </label>
                                        <input
                                            type="date"
                                            value={fromDate}
                                            min={new Date().toISOString().split("T")[0]}
                                            onChange={(e) => {
                                                const selectedDate = e.target.value;
                                                setFromDate(selectedDate);

                                                // If To Date is before From Date, reset To Date
                                                if (toDate && selectedDate > toDate) {
                                                    setToDate("");
                                                }

                                                // If same date and To Time is before From Time, reset To Time
                                                if (
                                                    selectedDate === toDate &&
                                                    fromTime &&
                                                    toTime &&
                                                    toTime < fromTime
                                                ) {
                                                    setToTime("");
                                                }
                                            }}
                                            className="w-full text-sm px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">
                                            From Time
                                        </label>
                                        <input
                                            type="time"
                                            value={fromTime}
                                            min={fromDate === new Date().toISOString().split("T")[0] ? new Date().toTimeString().slice(0, 5) : undefined}
                                            onChange={(e) => {
                                                const selectedTime = e.target.value;
                                                setFromTime(selectedTime);
                                                if (fromDate === toDate && toTime && selectedTime > toTime) {
                                                    setToTime("");
                                                }
                                            }}
                                            className="w-full text-sm px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">
                                            To Date
                                        </label>
                                        <input
                                            type="date"
                                            value={toDate}
                                            min={fromDate || new Date().toISOString().split("T")[0]}
                                            onChange={(e) => {
                                                const selectedDate = e.target.value;
                                                setToDate(selectedDate);
                                                if (selectedDate === fromDate && fromTime && toTime && toTime < fromTime) {
                                                    setToTime("");
                                                }
                                            }}
                                            className="w-full text-sm px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">
                                            To Time
                                        </label>
                                        <input type="time" value={toTime}
                                            min={toDate === fromDate && fromTime ? fromTime : undefined}
                                            onChange={(e) => setToTime(e.target.value)}
                                            className="w-full text-sm px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200"
                                        />
                                    </div>


                                    {(scheduleError || error) && (
                                        <>
                                            <p className="col-span-2 text-xs text-red-600">
                                                {scheduleError || error}
                                            </p>
                                        </>
                                    )}
                                </div>
                            )}

                        </>
                    )}

                    <div className="flex gap-3">
                        <button onClick={onClose}
                            className="flex-1 px-4 py-2.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium">
                            Cancel
                        </button>
                        <button onClick={handleAssign} disabled={submitting}
                            className="flex-1 px-4 py-2.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium">
                            {submitting ? 'Assigning…' : 'Assign'}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}

function CatalogTable({ items, loading, onViewAssign, typeLabel }) {
    if (loading) {
        return (
            <div className="divide-y divide-gray-100">
                {[1, 2, 3, 4, 5].map(i => (
                    <div key={i} className="flex items-center gap-4 px-5 py-4 animate-pulse">
                        <div className="w-4 h-4 bg-gray-200 rounded flex-shrink-0" />
                        <div className="flex-1 space-y-1.5">
                            <div className="h-3 bg-gray-200 rounded w-48" />
                            <div className="h-3 bg-gray-100 rounded w-28" />
                        </div>
                        <div className="h-5 w-24 bg-gray-100 rounded-full" />
                        <div className="h-5 w-16 bg-gray-100 rounded" />
                    </div>
                ))}
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className="py-16 flex flex-col items-center text-center text-gray-400">
                <BookOpen size={32} className="mb-3 opacity-30" />
                <p className="text-sm font-medium">No {typeLabel.toLowerCase()}s found</p>
                <p className="text-xs mt-1">Try adjusting your search or source filter</p>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
                <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{typeLabel}</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-28">Format</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-36">Source</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-24">Assigned</th>
                        <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-28">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                    {items.map(item => (
                        <tr key={item.sccpId} className="hover:bg-gray-50/60 transition-colors">
                            <td className="px-5 py-3.5">
                                <div className="flex items-center gap-2">
                                    {item.type === 'assessment'
                                        ? <ClipboardList size={15} className="text-purple-500 flex-shrink-0" />
                                        : <BookOpen size={15} className="text-blue-500 flex-shrink-0" />}
                                    <span className="font-medium text-gray-900">{item.title || '—'}</span>
                                </div>
                            </td>
                            <td className="px-4 py-3.5 text-gray-500 whitespace-nowrap">{fmt(item.format) || '—'}</td>
                            <td className="px-4 py-3.5">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${item.origin === 'org' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'}`}>
                                    {item.origin === 'org' ? 'Your Organisation' : 'keySkillset'}
                                </span>
                            </td>
                            <td className="px-4 py-3.5 text-gray-500">
                                {item.licensesAssigned > 0 ? item.licensesAssigned : '—'}
                            </td>
                            <td className="px-5 py-3.5 text-right">
                                <button onClick={() => onViewAssign(item)}
                                    className="text-xs font-medium text-purple-600 hover:text-purple-800 hover:underline whitespace-nowrap">
                                    View &amp; Assign
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function GlobalCatalog() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [courseTotal, setCourseTotal] = useState(0);
    const [assessmentTotal, setAssessmentTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [search, setSearch] = useState('');
    const [debSearch, setDebSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [activeType, setActiveType] = useState('course');
    const [activeOrigin, setActiveOrigin] = useState('');
    const [viewItem, setViewItem] = useState(null);
    const [toast, setToast] = useState('');
    const [isFullCreator, setIsFullCreator] = useState(false);
    const debRef = useRef(null);
    const stateRef = useRef({ type: 'course', origin: '', page: 0, search: '' });

    useEffect(() => {
        tenantUserSvc.getMe().then(r => {
            if (r.ok) setIsFullCreator(r.data?.featureToggleMode === 'FULL_CREATOR');
        });
    }, []);

    const load = useCallback((pg, q, typ, ori) => {
        setLoading(true);
        catalogSvc.getCatalog({ search: q, page: pg, size: PAGE_SIZE, type: typ, origin: ori })
            .then(r => {
                if (r.ok) {
                    setItems(r.data?.items || []);
                    setTotal(r.data?.total || 0);
                    setCourseTotal(r.data?.courseTotal || 0);
                    setAssessmentTotal(r.data?.assessmentTotal || 0);
                }
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        stateRef.current = { type: activeType, origin: activeOrigin, page, search: debSearch };
        load(page, debSearch, activeType, activeOrigin);
    }, [page, debSearch, activeType, activeOrigin, load]);

    const handleSearchChange = e => {
        const val = e.target.value;
        setSearch(val);
        clearTimeout(debRef.current);
        debRef.current = setTimeout(() => { setPage(0); setDebSearch(val); }, 350);
    };

    const handleTypeChange = key => { setActiveType(key); setPage(0); };
    const handleOriginChange = key => { setActiveOrigin(key); setPage(0); };

    const handleAssigned = (count) => {
        setViewItem(null);
        const s = stateRef.current;
        load(s.page, s.search, s.type, s.origin);
        if (typeof count === 'object') {
            const parts = [];
            if (count.enrolled) parts.push(`${count.enrolled} enrolled`);
            if (count.reactivated) parts.push(`${count.reactivated} reactivated`);
            setToast(parts.length ? `${parts.join(', ')} successfully.` : 'Done.');
        } else {
            setToast(`${count} learner(s) enrolled successfully.`);
        }
        setTimeout(() => setToast(''), 3500);
    };

    const TYPE_TABS = [
        { key: 'course', label: 'Courses', count: courseTotal, icon: BookOpen },
        { key: 'assessment', label: 'Assessments', count: assessmentTotal, icon: ClipboardList },
    ];

    const ORIGIN_TABS = [
        { key: '', label: 'All Content' },
        // { key: 'kss', label: 'keySkillset Content' },
        ...(isFullCreator ? [{ key: 'kss', label: 'keySkillset Content' }] : []),
        ...(isFullCreator ? [{ key: 'org', label: 'Your Organisation' }] : []),
    ];

    return (
        <Page title="Global Catalog">
            <div className="px-6 py-6">
                <div className="mb-6">
                    <h1 className="text-xl font-bold text-gray-900">Global Catalog</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Browse and assign courses &amp; assessments to your learners
                    </p>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="flex items-center justify-between border-b border-gray-200 px-5">
                        <div className="flex">
                            {TYPE_TABS.map(t => (
                                <button key={t.key} onClick={() => handleTypeChange(t.key)}
                                    className={`flex items-center gap-2 px-4 py-3.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${activeType === t.key
                                        ? 'border-purple-600 text-purple-700'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                        }`}>
                                    <t.icon size={15} />
                                    {t.label}
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${activeType === t.key
                                        ? 'bg-purple-100 text-purple-700'
                                        : 'bg-gray-100 text-gray-500'
                                        }`}>{t.count}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center justify-between px-5 py-2.5 bg-gray-50/60 border-b border-gray-100">
                        <div className="flex gap-1">
                            {ORIGIN_TABS.map(t => (
                                <button key={t.key} onClick={() => handleOriginChange(t.key)}
                                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${activeOrigin === t.key
                                        ? 'bg-purple-600 text-white shadow-sm'
                                        : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                                        }`}>
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        <div className="relative">
                            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                value={search}
                                onChange={handleSearchChange}
                                placeholder={`Search ${activeType === 'course' ? 'courses' : 'assessments'}…`}
                                className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200 w-48"
                            />
                        </div>
                    </div>

                    <CatalogTable
                        items={items}
                        loading={loading}
                        onViewAssign={setViewItem}
                        typeLabel={activeType === 'course' ? 'Course' : 'Assessment'}
                    />

                    {!loading && total > 0 && (
                        <Pagination page={page} total={total} size={PAGE_SIZE} onChange={setPage} />
                    )}
                </div>
            </div>

            {viewItem && (
                <ViewAssignDrawer
                    item={viewItem}
                    onClose={() => setViewItem(null)}
                    onAssigned={handleAssigned}
                />
            )}

            {toast && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-green-600 text-white text-sm px-5 py-2.5 rounded-lg shadow-lg z-50">
                    {toast}
                </div>
            )}
        </Page>
    );
}